/**
 * Cuerpo académico del landing: qué se muestra, en qué orden y qué se guarda.
 *
 * Es contenido de presentación cargado a mano en Configuración → Landing. El
 * orden de la lista es el orden de la página; las autoridades suben a la fila
 * destacada sin perder su orden relativo.
 */
import type { FacultyMember } from '@dosfilos/domain';
import { safeImageUrl } from './featured-events';

export const MAX_FACULTY = 40;
export const MAX_FACULTY_BIO = 500;

export type FacultyError = 'name' | 'role';

export function validateFacultyMember(m: FacultyMember): FacultyError[] {
  const errors: FacultyError[] = [];
  if (!m.name?.trim()) errors.push('name');
  if (!m.role?.trim()) errors.push('role');
  return errors;
}

/** Lo que el landing muestra: visibles y completos; autoridades primero. */
export function visibleFaculty(list: readonly FacultyMember[] | null | undefined): FacultyMember[] {
  const shown = (list ?? []).filter((m) => m.visible && validateFacultyMember(m).length === 0);
  return [...shown.filter((m) => m.leadership), ...shown.filter((m) => !m.leadership)];
}

/**
 * Lo que se guarda: textos recortados, áreas sin vacíos y opcionales vacíos
 * OMITIDOS (Firestore no acepta `undefined`, y un solo campo así hace fallar el
 * guardado entero de Configuración). Una foto con URL insegura se descarta.
 */
export function normalizeFaculty(list: readonly FacultyMember[]): FacultyMember[] {
  return list.slice(0, MAX_FACULTY).map((m) => {
    const degree = m.degree?.trim();
    const bio = m.bio?.trim().slice(0, MAX_FACULTY_BIO);
    const areas = (m.areas ?? []).map((a) => a.trim()).filter(Boolean);
    const photoUrl = safeImageUrl(m.photoUrl);
    return {
      id: m.id,
      name: m.name.trim(),
      role: m.role.trim(),
      leadership: m.leadership,
      visible: m.visible,
      ...(degree ? { degree } : {}),
      ...(areas.length ? { areas } : {}),
      ...(bio ? { bio } : {}),
      ...(photoUrl ? { photoUrl } : {}),
    };
  });
}

export function newFacultyMember(id: string): FacultyMember {
  return { id, name: '', role: '', leadership: false, visible: true };
}

const HONORIFICS = /^(dr|dra|rev|ps|pr|pbro|prof|hno|hna|mg|lic|sr|sra)\.?$/i;

/** Iniciales para el retrato de quien no tiene foto: sin «Dr.», «Rev.», «Ps.»… */
export function initials(name: string): string {
  const words = name
    .trim()
    .split(/\s+/)
    .filter((w) => w && !HONORIFICS.test(w));
  return words
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');
}

/** Sube o baja un docente en la lista, sin salirse de los bordes. */
export function moveFaculty<T>(list: readonly T[], index: number, dir: -1 | 1): T[] {
  const target = index + dir;
  if (index < 0 || index >= list.length || target < 0 || target >= list.length) return [...list];
  const next = [...list];
  [next[index], next[target]] = [next[target]!, next[index]!];
  return next;
}

/** «Griego, Exégesis» → ['Griego', 'Exégesis']. */
export function parseAreas(text: string): string[] {
  return text
    .split(',')
    .map((a) => a.trim())
    .filter(Boolean);
}
